import { createFx, type FxInstance, type PixelFrame } from "pixelfx";
import { ART_FLOOR, toArtX, toArtY } from "../shared/screen";
import { FIGHTER, FULL_METER, type FighterState, type MatchState, type SimEvent } from "../sim";
import { FX_LIBRARY } from "./library";

interface LiveFx {
  readonly instance: FxInstance;
  readonly layer: "back" | "front";
}

const STEP = 1 / 60;
/** Удар ждёт своего кадра на картинке не дольше стольких кадров экрана. */
const PLACE_WAIT = 8;

/** Точка касания удара на нарисованной позе (render/stage3d.ts strikePoint). */
export interface TouchPoint {
  readonly x: number;
  readonly y: number;
  readonly bone: string;
  /** От бьющей кости до тела соперника в плоскости кадра, пиксели мира. */
  readonly touch: number;
}

/** Где встали искры удара: кадр мира события, чей удар, точка арта, кость, касание. */
export interface SparkPlace {
  readonly frame: number;
  readonly side: number;
  readonly x: number;
  readonly y: number;
  readonly bone: string;
  readonly touch: number;
  readonly ability: string;
}

interface PendingFx {
  readonly event: SimEvent;
  readonly flip: boolean;
  readonly ability: number;
  readonly abilityFrame: number;
  waited: number;
}

/** Аура Ярости: новый вихрь каждые столько кадров. */
const AURA_EVERY = 6;

/**
 * Эффекты pixelfx по событиям мира, в сетке арта. Украшение, не часть мира: время эффектов идёт шагами мира по 1/60 с,
 * поэтому картинка одинакова при любой частоте экрана и в записи ролика.
 */

export class FxSystem {
  /**
   * Частицы без события (угольки ауры Ярости). В 3D выключены: Джон 04.11.2026 — «эти искры вокруг… просто блинкают»;
   * там частицы — только от события и в его точке.
   */
  ambient = true;
  /**
   * Точка эффекта на теле в 3D (Джон 04.11.2026, 22:52: «частицы для мощного удара… не на руке»): кисть или стопа
   * модели, ближайшая к точке события, в сетке арта. null — точка из события (2D).
   */
  anchor: ((side: number, x: number, y: number) => readonly [number, number] | null) | null = null;
  /** 3D: удары и блоки — в точке касания по нарисованной позе (place), а не в миг события. */
  touch = false;
  private pending: PendingFx[] = [];
  /** Где встали искры ударов — для замера в съёмке (tools/capture/sparks.py); съёмка забирает и чистит. */
  readonly sparks: SparkPlace[] = [];
  private live: LiveFx[] = [];
  /** Тряска экрана: сколько кадров ещё и с какой силой, пиксели. */
  shakeFrames = 0;
  shakePower = 0;

  spawn(id: string, x: number, y: number, flip: boolean, seed: number, layer: "back" | "front" = "front"): void {
    const entry = FX_LIBRARY.get(id);
    if (!entry) return;
    const options = { ...entry.options, x, y, flip, seed, floor: ART_FLOOR - y };
    this.live.push({ instance: createFx(entry.module, options), layer });
  }

  private shake(frames: number, power: number): void {
    this.shakeFrames = Math.max(this.shakeFrames, frames);
    this.shakePower = Math.max(this.shakePower, power);
  }

  onEvents(events: readonly SimEvent[], state: MatchState): void {
    this.rageAura(state);
    for (const event of events) {
      const attacker = event.who === 0 || event.who === 1 ? state.fighters[event.who] : null;
      const flip = (attacker?.facing ?? 1) < 0;
      // снаряд бьёт сам по себе: его точка — место попадания, а не кисть бойца
      const shot = (event.fx?.startsWith("magic/") ?? false) || event.point === true;
      const bodyEvent =
        event.kind === "hit" ||
        event.kind === "block" ||
        (event.fx?.startsWith("charge/") ?? false) ||
        (event.fx?.startsWith("fire/") ?? false);
      // 3D: удар и блок ждут, пока картинка дойдёт до кадра попадания, и встают в точку касания (place)
      if (this.touch && !shot && attacker && (event.kind === "hit" || event.kind === "block")) {
        this.pending.push({ event, flip, ability: attacker.ability, abilityFrame: attacker.abilityFrame, waited: 0 });
        continue;
      }
      const onBody = bodyEvent && !shot ? this.anchor?.(event.who, toArtX(event.x), toArtY(event.y)) : null;
      this.play(event, onBody ? onBody[0] : toArtX(event.x), onBody ? onBody[1] : toArtY(event.y), flip, attacker);
    }
  }

  /**
   * Удары и блоки, ждущие своего кадра (консультант 05.11.2026: «искры и вспышка — в точке касания… по позе
   * отрисовки»): display — снимок, который сейчас нарисован (в замедлении он отстаёт от мира), point — точка касания
   * бьющего на этой позе (render/stage3d.ts strikePoint). Картинка дошла до кадра удара — эффект встаёт в точку.
   * Возвращает, сколько встало: слой эффектов этого кадра надо перерисовать, чтобы искра была на той же позе.
   */
  place(display: MatchState, point: (side: number) => TouchPoint | null): number {
    const waiting: PendingFx[] = [];
    let placed = 0;
    for (const item of this.pending) {
      const { event } = item;
      const attacker = display.fighters[event.who === 1 ? 1 : 0];
      const reached =
        attacker.state !== FIGHTER.ability ||
        attacker.ability !== item.ability ||
        attacker.abilityFrame >= item.abilityFrame - 0.001 ||
        item.waited >= PLACE_WAIT;
      if (!reached) {
        item.waited++;
        waiting.push(item);
        continue;
      }
      const touch = point(event.who);
      const x = touch ? touch.x : toArtX(event.x);
      const y = touch ? touch.y : toArtY(event.y);
      this.play(event, x, y, item.flip, attacker);
      placed++;
      this.sparks.push({
        frame: event.frame,
        side: event.who,
        x,
        y,
        bone: touch?.bone ?? "",
        touch: touch?.touch ?? -1,
        ability: event.ability ?? "",
      });
    }
    this.pending = waiting;
    return placed;
  }

  /** Эффекты события в точке (x, y) сетки арта. */
  private play(event: SimEvent, x: number, y: number, flip: boolean, attacker: FighterState | null): void {
    switch (event.kind) {
      case "hit":
        // финал ветки комбо — искра тяжёлого удара и взрыв частиц своего цвета, экран трясёт сильнее
        if (event.fx?.startsWith("finale/")) {
          this.spawn("heavy/spark", x, y, flip, event.frame);
          this.spawn(event.fx, x, y, flip, event.frame + 2);
          this.shake(14, 3);
        } else this.spawn(event.fx ?? "impacts/hit-spark", x, y, flip, event.frame);
        if (event.strength >= 1) this.spawn("blood/burst", x, y, flip, event.frame + 7);
        // сильный удар — волна частиц от точки попадания (приём моушен-графики)
        if (event.strength >= 1) this.spawn("particles/wave", x, y, flip, event.frame + 3);
        // самый сильный (заряд, добивание) — ещё и взрыв частиц из точки удара
        if (event.strength >= 2) this.spawn("particles/burst", x, y, flip, event.frame + 1);
        if (event.strength >= 2) this.shake(10, 2);
        else if (event.strength === 1) this.shake(6, 1);
        break;
      case "block":
        this.spawn(event.fx ?? "block/spark", x, y, !flip, event.frame);
        break;
      case "land":
        if (event.strength >= 2) {
          this.spawn("dust/landing", x, ART_FLOOR, false, event.frame, "back");
          this.shake(8, 1);
        }
        break;
      case "superStart":
        this.spawn("particles/gather", x + (attacker?.facing ?? 1) * 50, toArtY(-110), flip, event.frame);
        break;
      case "superHit":
        this.spawn("particles/vortex", x, toArtY(-100), flip, event.frame);
        this.shake(8, 1);
        break;
      case "superFinale":
        this.spawn("particles/burst", x, toArtY(-100), flip, event.frame);
        this.shake(16, 3);
        break;
      case "ability":
      case "action":
        // скачок-пыльца рисуется облаком частиц из пикселей бойца (render/teleportCloud.ts) — здесь ничего
        if (event.fx === "teleport/out") break;
        if (event.fx === "teleport/in") {
          // появление: мягкий сбор частиц у тела и волна по полу
          this.spawn("teleport/gather", x, y, flip, event.frame);
          this.spawn("blink/spark", x, y, flip, event.frame + 1);
          this.spawn("particles/wave", x, toArtY(0), flip, event.frame + 2);
          break;
        }
        // точка из события: телепорт — на уровне груди, заряд — у кулака или стопы, выравнивание — где боец в воздухе
        if (event.fx) this.spawn(event.fx, x, y, flip, event.frame);
        break;
      default:
        break;
    }
  }

  /**
   * Аура Ярости: полная шкала — вокруг бойца крутится огненный вихрь (позади него), каждые AURA_EVERY кадров новый.
   * Видно без HUD, что суперприём готов. Только картинка: читает снимок мира, ничего в нём не меняет.
   */
  private rageAura(state: MatchState): void {
    if (!this.ambient || state.frame % AURA_EVERY !== 0 || state.cinematic >= 0) return;
    state.fighters.forEach((fighter, side) => {
      if (fighter.meter < FULL_METER || fighter.health <= 0) return;
      // вихри по очереди зеркально — облако вокруг бойца, а не хвостом в одну сторону
      const mirror = Math.floor(state.frame / AURA_EVERY) % 2 === 0;
      this.spawn(
        "aura/rage",
        toArtX(fighter.x / 256),
        toArtY(fighter.y / 256) - 135,
        mirror,
        state.frame + side,
        "back",
      );
    });
  }

  /** Шаг эффектов на кадр мира; rate < 1 — замедление решающего удара: частицы летят в такт телам. */
  update(rate = 1): void {
    for (const fx of this.live) fx.instance.update(STEP * rate);
    this.live = this.live.filter((fx) => !fx.instance.done());
    if (this.shakeFrames > 0) this.shakeFrames--;
    else this.shakePower = 0;
  }

  /** За бойцами: задние половины всех эффектов и целиком те, что лежат на полу. */
  drawBehind(frame: PixelFrame): void {
    for (const fx of this.live) {
      fx.instance.draw(frame, "back");
      if (fx.layer === "back") fx.instance.draw(frame, "front");
    }
  }

  /** Перед бойцами: передние половины эффектов. */
  drawFront(frame: PixelFrame): void {
    for (const fx of this.live) if (fx.layer === "front") fx.instance.draw(frame, "front");
  }

  clear(): void {
    this.live = [];
    this.pending = [];
  }
}
